/**
 * The phone recorder's one layout system (docs/mobile-editor-layout-spec.md
 * section 15): the tokens every recorder screen uses, and where each control
 * sits in each of the four screens. Pure, for unit tests.
 *
 * The design target is the iPhone 12 Pro Max in landscape with Safari's bars
 * hidden (926 x 428, safe areas 47 left and right, 21 at the bottom). With
 * the bars showing (926 x 300) the landscape screens take the fallbacks
 * marked "squeeze" below.
 */
import { DEFAULT_ASPECT, fitAspect, type PhoneViewport } from './phoneLayout.ts';

/** Section 15 tokens. */
export const REC = {
  /** Touch target of every recorder control. */
  target: 44,
  /** Between buttons in a column or a row. */
  gap: 8,
  /** Outer padding, inside the safe areas. */
  pad: 12,
  /** Squeeze: a column that does not fit at 44 and 8. */
  tightTarget: 36,
  tightGap: 6,
  /** Record/Save, always the last control in its group. */
  record: 64,
  /** Inside a glass bar, round its buttons. */
  pillPad: 4,
  /** Inside a landscape column: 44 buttons in a 56 pill (15.13). */
  colPad: 6,
  /** The collapse chevron at the top of each column (15.14). */
  chevronH: 28,
  /** Side by side rows (15.20): the speed chip's and mute's width. */
  pairChip: 36,
  /** Side by side rows (15.20): the scrubber's share of the row, at least. */
  pairScrubMin: 0.35,
  /** Landscape, one video: the timeline pill. */
  timelineH: 40,
  timelineInset: 8,
  /** Titles: Subhead 15/20 semibold, at most this wide. */
  titleMax: 120,
  titleH: 20,
  titleGap: 4,
  /** Nothing else within this of the close X (15.7). */
  xClear: 56,
  /** Side by side in landscape: between the two videos (15.29). */
  gutter: 8,
  /** Portrait: each side of the video block (15.29). */
  portraitSide: 16,
  /** Side by side in portrait: from one video's row to the next title. */
  stackSpace: 24,
  /** The portrait header. */
  header: 44,
} as const;

/** Times are SF Mono at Subhead 15: 0.6 of the size a character. */
export const MONO_CHAR = 9;

export interface Box { left: number; top: number; w: number; h: number }

/** "m:ss / m:ss" for a segment of this many seconds: its width in characters. */
export function readoutChars(lengthSeconds: number): number {
  const s = Math.max(0, Math.floor(Number.isFinite(lengthSeconds) ? lengthSeconds : 0));
  const one = `${Math.floor(s / 60)}:00`.length;
  return 2 * one + 3;
}

/**
 * A side by side row (15.20): play, the scrubber, the time, speed and mute,
 * with the scrubber at least 35 percent of the row. The narrowest row that
 * holds that; a video narrower than this gets a row this wide, centred on
 * it, so nothing is cut off at the right.
 */
export function pairRowMinW(chars: number): number {
  const fixed = REC.target + chars * MONO_CHAR + 2 * REC.pairChip + 4 * REC.gap;
  return Math.ceil(fixed / (1 - REC.pairScrubMin));
}

/**
 * A row of `n` controls across `rowW` (portrait tools row, 15.3): the gap
 * between them, 8 where that fits, down to 6 where it does not. At 428 the
 * eight controls of the one-video row get 7.4.
 */
export function rowGap(rowW: number, n: number): number {
  if (n < 2) return REC.gap;
  return Math.max(REC.tightGap, Math.min(REC.gap, (rowW - n * REC.target) / (n - 1)));
}

/** Landscape, one video (15.1, 15.7, 15.8, 15.9, 15.12 to 15.14). */
export interface LandscapeSingle {
  video: Box;
  /** The close X, alone at the top left. */
  x: Box;
  /** Whether the tools are split into a left and a right column. */
  split: boolean;
  /** Button size and gap in the columns (44 and 8, or the squeeze 36 and 6). */
  size: number;
  gap: number;
  /** Padding inside a column's pill. */
  pad: number;
  /**
   * The left column: its pill (chevron, then the tools; all of them when not
   * split), then Record/Save 8 below it, then during a take the trash 8
   * below that. `w` is the pill's; Record/Save is wider and centred on it.
   * `h` is the tallest it gets (during a take), so nothing moves when a take
   * starts.
   */
  left: Box;
  /**
   * Squeeze: the tallest the left pill may be so Record/Save and the trash
   * stay on screen; its tools scroll inside it beyond that. Null when the
   * column fits.
   */
  leftPillMax: number | null;
  /**
   * The left column anchored from the bottom (15.27): the distance from
   * the viewport's bottom to the column's bottom, and the most height the
   * column may take. Positioned this way, Record/Save and the trash stay on
   * screen even when the visible height shrinks under the layout (Safari's
   * bars coming back as the camera starts); the pill gives way instead.
   */
  leftBottom: number;
  leftMaxH: number;
  /** The right column's pill when split: chevron, speed, mute, camera, compare (not during a take). */
  right: Box | null;
  /** The timeline pill. */
  pill: Box;
  /** The recording timer's top, centred over the video. */
  timerTop: number;
}

/** A column pill: the chevron, then n buttons, padded. */
export const columnPillH = (n: number, size: number, gap: number, pad: number) =>
  2 * pad + REC.chevronH + (n > 0 ? gap + n * size + (n - 1) * gap : 0);
/** Below the left pill: Record/Save 8 below it, and in a take the trash 8 below that. */
const belowPill = (size: number, recording: boolean) => REC.gap + REC.record + (recording ? REC.gap + size : 0);

/** Every tool but the close X, idle: marker, eraser, undo, redo, speed, mute, camera, compare. */
const ALL_TOOLS = 8;
const LEFT_TOOLS = 4;
const RIGHT_TOOLS = 4;

export function landscapeSingleLayout(v: PhoneViewport, aspect: number = DEFAULT_ASPECT): LandscapeSingle {
  const { top: T, right: R, bottom: B, left: L } = v.insets;
  /* The video: the full visible height at its own aspect, centred. Nothing
     beside it takes width from it, so nothing resizes it. */
  const fit = fitAspect(aspect, v.w, v.h);
  const video = { left: (v.w - fit.w) / 2, top: (v.h - fit.h) / 2, w: fit.w, h: fit.h };

  /* The left column at its tallest: during a take, the trash under
     Record/Save (one column: the compare control leaves the pill then). */
  const leftH = (n: number, size: number, gap: number, pad: number) => Math.max(
    columnPillH(n, size, gap, pad) + belowPill(size, false),
    columnPillH(n === ALL_TOOLS ? n - 1 : n, size, gap, pad) + belowPill(size, true),
  );
  /* Try 44 and 8 with the X's 56 clear, then only 8 clear of the X, then
     the squeeze (36 and 6), then into the bottom padding and safe area. */
  const steps = [
    { size: REC.target, gap: REC.gap, clear: REC.xClear, bottom: B + REC.pad },
    { size: REC.target, gap: REC.gap, clear: REC.gap, bottom: B + REC.pad },
    { size: REC.tightTarget, gap: REC.tightGap, clear: REC.xClear, bottom: B + REC.pad },
    { size: REC.tightTarget, gap: REC.tightGap, clear: REC.gap, bottom: B + REC.pad },
    { size: REC.tightTarget, gap: REC.tightGap, clear: REC.gap, bottom: B },
    { size: REC.tightTarget, gap: REC.tightGap, clear: REC.gap, bottom: 0 },
  ];
  const pad = REC.colPad;
  const xTop = T + REC.pad;
  const xBottom = xTop + REC.target;
  const room = (s: typeof steps[number]) => v.h - s.bottom - (xBottom + s.clear);
  let split = false;
  let step = steps.find((s) => leftH(ALL_TOOLS, s.size, s.gap, pad) <= room(s) && s.size === REC.target && s.clear === REC.xClear);
  if (!step) {
    split = true;
    step = steps.find((s) => leftH(LEFT_TOOLS, s.size, s.gap, pad) <= room(s)) ?? steps[steps.length - 1];
  }
  const n = split ? LEFT_TOOLS : ALL_TOOLS;
  const colW = step.size + 2 * pad;
  const full = leftH(n, step.size, step.gap, pad);
  const fits = full <= room(step);
  const h = fits ? full : room(step);
  const leftPillMax = fits ? null : Math.max(REC.chevronH + 2 * pad, room(step) - belowPill(step.size, true));
  const regionTop = xBottom + step.clear;
  const left = { left: L + REC.pad, top: regionTop + Math.max(0, (room(step) - h) / 2), w: colW, h };
  /* The X sits over the column's centre line. */
  const x = { left: left.left + (colW - REC.target) / 2, top: xTop, w: REC.target, h: REC.target };

  let right: Box | null = null;
  if (split) {
    const rh = columnPillH(RIGHT_TOOLS, step.size, step.gap, pad);
    const top0 = T + REC.pad;
    const bottom0 = v.h - B - REC.pad;
    right = { left: v.w - R - REC.pad - colW, top: top0 + Math.max(0, (bottom0 - top0 - rh) / 2), w: colW, h: rh };
  }

  /* The pill: 8 in from the video's sides, and 8 above its bottom edge or
     8 above the bottom safe area, whichever is higher. It keeps 8 clear of
     a column it would otherwise run under (the left column's widest part is
     Record/Save). */
  const pillBottom = Math.min(video.top + video.h - REC.timelineInset, v.h - B - REC.timelineInset);
  const pillTop = pillBottom - REC.timelineH;
  let pl = video.left + REC.timelineInset;
  let pr = video.left + video.w - REC.timelineInset;
  const leftSpan = { left: left.left - Math.max(0, (REC.record - colW) / 2), w: Math.max(REC.record, colW), top: left.top, h: left.h };
  for (const c of [leftSpan, right]) {
    if (!c || c.top >= pillBottom || c.top + c.h <= pillTop) continue;
    if (c.left + c.w / 2 < v.w / 2) pl = Math.max(pl, c.left + c.w + REC.gap);
    else pr = Math.min(pr, c.left - REC.gap);
  }
  const pill = { left: pl, top: pillTop, w: Math.max(0, pr - pl), h: REC.timelineH };

  const leftBottom = v.h - (left.top + left.h);
  const leftMaxH = room(step);
  return { video, x, split, size: step.size, gap: step.gap, pad, left, leftPillMax, leftBottom, leftMaxH, right, pill, timerTop: Math.max(video.top, T) + REC.pad };
}

/** Landscape, side by side (15.2, 15.10). */
export interface LandscapePair {
  /** The box each video fits its own aspect into. */
  maxW: number;
  maxH: number;
  /** The bottom bar. */
  bar: Box;
  /** Where the pair's rows end (the videos are bottom-aligned above them). */
  rowsBottom: number;
}

/** The bottom bar: Record/Save inside it, padded. */
export const BAR_H = REC.record + 2 * REC.pillPad;

export function landscapePairLayout(v: PhoneViewport): LandscapePair {
  const { top: T, right: R, bottom: B, left: L } = v.insets;
  /* Spec 15.29: the panels run to the safe areas, no extra padding, 8
     between them; the bar keeps the 12. */
  const inner = v.w - L - R;
  const bar = { left: L + REC.pad, top: v.h - B - REC.pad - BAR_H, w: inner - 2 * REC.pad, h: BAR_H };
  const rowsBottom = bar.top - REC.gap;
  const maxW = Math.floor((inner - REC.gutter) / 2);
  const maxH = Math.max(60, rowsBottom - (T + REC.pad) - REC.titleH - REC.titleGap - REC.target);
  return { maxW, maxH, bar, rowsBottom };
}

/** Portrait record zone: Record/Save with the outer padding above and below. */
export const RECORD_ZONE_H = REC.record + 2 * REC.pad;

/** No height bound: the width decides (15.29). */
export const UNBOUNDED_H = 1e6;

/**
 * Portrait (15.29): the box a video fits in. Width wins: the full content
 * width, 16 each side, one video or side by side, with no height bound.
 * The stack scrolls inside its area when it is taller than the space above
 * the tools row; nothing is scaled down to fit.
 */
export function portraitBounds(v: PhoneViewport, sideBySide: boolean): { maxW: number; maxH: number } {
  void sideBySide;
  const w = v.w - v.insets.left - v.insets.right - 2 * REC.portraitSide;
  return { maxW: Math.max(0, w), maxH: UNBOUNDED_H };
}
